import { fetchEventSource, type EventSourceMessage } from '@microsoft/fetch-event-source'
import type { QueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { API_BASE, getStudioUser, STUDIO_USER_HEADER } from '@/api/client'
import { keys } from '@/api/keys'
import type { JobEvent } from '@/schema'
import { useQueueStore, type AiHealthSnapshot, type JobProgressEvent, type QueueStats } from '@/stores/queueStore'
import { useVocabStore } from '@/stores/vocabStore'

/**
 * Server-sent events (§5.6).
 *
 * `openGlobalEvents()` keeps ONE `/api/events` connection per *visible* tab and
 * fans each event into the query cache (invalidations) and `queueStore`. A
 * hidden tab lets go of it: a browser allows only six HTTP/1.1 connections per
 * host across ALL tabs, so a few background studio tabs (plus a story's video)
 * used to starve the visible one, whose requests then hung ("Opening…" forever).
 * `fetch-event-source` sends `Last-Event-ID` on reconnect by itself, so a
 * flaky link replays what was missed instead of dropping it.
 *
 * `subscribeJob()` opens a per-job stream (`/api/jobs/{id}/events`) for
 * screens that render a live phase list — generation progress, the bulk
 * annotate dialog — and closes it with the returned disposer.
 */

/* ------------------------------------------------------------ event types */

export interface ProposalReadyEvent {
  asset_uid: string
  proposal_id: number
}
export interface DocUpdatedEvent {
  asset_uid: string
  rev: number
  by: string
}
export interface AiHealthEvent extends AiHealthSnapshot {}
export interface IndexDoneEvent {
  run_id: number
}
/** `backend/app/vocab/routes.py` `_publish`: a kind was added, changed, hidden, reordered… anywhere. */
export interface VocabChangedEvent {
  /** `settings.vocab_rev` after the write; `null` when the write did not bump it. */
  rev: number | null
  action: string
  domain: string | null
  ids: string[]
  by: string
}

export type GlobalEvent =
  | { type: 'job.progress'; data: JobProgressEvent }
  | { type: 'proposal.ready'; data: ProposalReadyEvent }
  | { type: 'doc.updated'; data: DocUpdatedEvent }
  | { type: 'queue.stats'; data: QueueStats }
  | { type: 'ai.health'; data: AiHealthEvent }
  | { type: 'index.done'; data: IndexDoneEvent }
  | { type: 'vocab.changed'; data: VocabChangedEvent }

const GLOBAL_EVENT_TYPES: ReadonlySet<string> = new Set(['job.progress', 'proposal.ready', 'doc.updated', 'queue.stats', 'ai.health', 'index.done', 'vocab.changed'])

class FatalSseError extends Error {}

function parseData<T>(msg: EventSourceMessage): T | null {
  if (!msg.data) return null
  try {
    return JSON.parse(msg.data) as T
  } catch {
    console.warn('[sse] unparseable event', msg.event, msg.data.slice(0, 120))
    return null
  }
}

function headers(): Record<string, string> {
  return { Accept: 'text/event-stream', [STUDIO_USER_HEADER]: getStudioUser() }
}

/* ------------------------------------------------------------- dispatch */

/** Apply one global event to the query cache and the queue store. Exported for tests. */
export function dispatchGlobalEvent(qc: QueryClient, ev: GlobalEvent): void {
  const queue = useQueueStore.getState()
  switch (ev.type) {
    case 'job.progress':
      queue.applyProgress(ev.data)
      if (ev.data.status && ev.data.status !== 'running') {
        void qc.invalidateQueries({ queryKey: keys.jobs.all() })
      }
      return
    case 'queue.stats':
      queue.setStats({ ...queue.stats, ...ev.data })
      return
    case 'ai.health':
      queue.setAiHealth(ev.data)
      void qc.invalidateQueries({ queryKey: keys.health() })
      return
    case 'proposal.ready':
      void qc.invalidateQueries({ queryKey: keys.proposals.all() })
      void qc.invalidateQueries({ queryKey: keys.assets.detail(ev.data.asset_uid) })
      void qc.invalidateQueries({ queryKey: keys.assets.list() })
      return
    case 'doc.updated':
      void qc.invalidateQueries({ queryKey: keys.annotation.doc(ev.data.asset_uid) })
      void qc.invalidateQueries({ queryKey: keys.annotation.revisions(ev.data.asset_uid) })
      void qc.invalidateQueries({ queryKey: keys.assets.detail(ev.data.asset_uid) })
      return
    case 'index.done':
      void qc.invalidateQueries({ queryKey: keys.index.all() })
      void qc.invalidateQueries({ queryKey: keys.assets.all() })
      void qc.invalidateQueries({ queryKey: ['categories'] })
      void qc.invalidateQueries({ queryKey: keys.stories.presets() })
      return
    case 'vocab.changed': {
      // Someone (another tab, another person, the AI "guess what these do") changed the list of
      // kinds: refetch it so new kinds just appear in the annotator's pickers. This tab's own
      // edits already moved the local rev forward, so their echo costs nothing.
      const rev = typeof ev.data.rev === 'number' ? ev.data.rev : null
      if (rev === null || rev > useVocabStore.getState().rev) void qc.invalidateQueries({ queryKey: keys.vocab.all() })
      return
    }
  }
}

/* ----------------------------------------------------------- connections */

let globalAbort: AbortController | null = null

/**
 * Open (or reuse) the tab's `/api/events` stream. Idempotent: a second call
 * while one is open is a no-op. Returns a disposer that closes it.
 */
export function openGlobalEvents(qc: QueryClient, opts: { since?: string } = {}): () => void {
  if (globalAbort) return closeGlobalEvents
  const ctrl = new AbortController()
  globalAbort = ctrl
  const setConnected = (v: boolean) => useQueueStore.getState().setConnected(v)
  const url = `${API_BASE}/api/events${opts.since ? `?since=${encodeURIComponent(opts.since)}` : ''}`

  void fetchEventSource(url, {
    signal: ctrl.signal,
    headers: headers(),
    // Reconnects with Last-Event-ID when the tab is shown again, so nothing is missed.
    openWhenHidden: false,
    async onopen(res) {
      if (res.ok && (res.headers.get('content-type') ?? '').includes('text/event-stream')) {
        setConnected(true)
        return
      }
      // 4xx is a programming error; do not hammer the server.
      if (res.status >= 400 && res.status < 500) throw new FatalSseError(`events: HTTP ${res.status}`)
      throw new Error(`events: HTTP ${res.status}`) // retried
    },
    onmessage(msg) {
      if (!msg.event || !GLOBAL_EVENT_TYPES.has(msg.event)) return
      const data = parseData<GlobalEvent['data']>(msg)
      if (data === null) return
      dispatchGlobalEvent(qc, { type: msg.event, data } as GlobalEvent)
    },
    onclose() {
      setConnected(false)
      throw new Error('events: closed') // triggers the library's reconnect
    },
    onerror(err) {
      setConnected(false)
      if (err instanceof FatalSseError) throw err
      return 2_000 // retry delay, ms
    },
  }).catch(() => setConnected(false))

  return closeGlobalEvents
}

export function closeGlobalEvents(): void {
  globalAbort?.abort()
  globalAbort = null
  useQueueStore.getState().setConnected(false)
}

/**
 * Per-job stream. `onEvent` receives every `JobEvent` (queued → … →
 * succeeded/failed/cancelled); the stream closes itself after a terminal
 * kind. Returns a disposer.
 */
export function subscribeJob(jobId: number, onEvent: (ev: JobEvent) => void, opts: { lastEventId?: string } = {}): () => void {
  const ctrl = new AbortController()
  const h = headers()
  if (opts.lastEventId) h['Last-Event-ID'] = opts.lastEventId

  void fetchEventSource(`${API_BASE}/api/jobs/${jobId}/events`, {
    signal: ctrl.signal,
    headers: h,
    openWhenHidden: true,
    onmessage(msg) {
      const kind = msg.event as JobEvent['kind'] | ''
      if (!kind) return
      const data = parseData<JobEvent['data']>(msg) ?? {}
      const seq = Number(msg.id) || 0
      onEvent({ job_id: jobId, seq, ts: new Date().toISOString(), kind, data } as JobEvent)
      if (kind === 'succeeded' || kind === 'failed' || kind === 'cancelled') ctrl.abort()
    },
    onerror(err) {
      if (err instanceof FatalSseError) throw err
      return 2_000
    },
  }).catch(() => undefined)

  return () => ctrl.abort()
}

/* ----------------------------------------------------------------- hooks */

/** Mount once near the root: keeps the global stream open for the tab's lifetime. */
export function useGlobalEvents(qc: QueryClient): void {
  useEffect(() => openGlobalEvents(qc), [qc])
}

/** Subscribe to a job's events while mounted; `jobId` `null` = no stream. */
export function useJobEvents(jobId: number | null, onEvent: (ev: JobEvent) => void): void {
  useEffect(() => {
    if (jobId == null) return
    return subscribeJob(jobId, onEvent)
    // onEvent is intentionally not a dependency: callers pass fresh closures each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])
}
